/**
 * V600: Canonical connection path summary — single source of truth for degree + mutual count.
 */

export type ConnectionPathSummaryResult = {
  degree: number;
  mutualFriendCount: number;
  label: string;
};

export type ResolveConnectionPathSummaryInput = {
  profile?: Record<string, unknown> | null;
  networkConnection?: Record<string, unknown> | null;
  connectionPath?: unknown[] | null;
  avatarChainData?: unknown[] | null;
  currentUserId?: string;
  targetUserId?: string;
};

export function formatDegree(degree: number): string {
  const n = Math.max(1, Math.floor(Math.abs(degree)));
  if (n === 1) return '1st degree';
  if (n === 2) return '2nd degree';
  if (n === 3) return '3rd degree';
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th degree`;
  switch (n % 10) {
    case 1:
      return `${n}st degree`;
    case 2:
      return `${n}nd degree`;
    case 3:
      return `${n}rd degree`;
    default:
      return `${n}th degree`;
  }
}

export function formatMutualFriends(count: number | null | undefined): string {
  if (count === undefined || count === null || count < 1) return '';
  return count === 1 ? '1 mutual friend' : `${count} mutual friends`;
}

type PathNode = Record<string, unknown>;
type MutualSource = Record<string, unknown> | null | undefined;

function nodeDedupeKey(node: PathNode): string | null {
  const id = node.user_id ?? node.userId ?? node.id;
  if (typeof id === 'string' && id.length > 0) return id;
  const name = node.name;
  if (typeof name === 'string' && name.length > 0 && name.trim().toLowerCase() !== 'you') {
    return `name:${name.trim().toLowerCase()}`;
  }
  return null;
}

function isViewerNode(node: PathNode): boolean {
  if (node.isCurrentUser === true) return true;
  if (node.degree === 0 || node.degree === '0') return true;
  const name = typeof node.name === 'string' ? node.name.trim().toLowerCase() : '';
  return name === 'you';
}

function isTargetNode(node: PathNode, targetId?: string): boolean {
  if (node.isTargetUser === true) return true;
  if (node.degree === 3 || node.degree === '3') return true;
  if (targetId) {
    const id = node.user_id ?? node.userId ?? node.id;
    if (typeof id === 'string' && id === targetId) return true;
  }
  return false;
}

function isDegree1Node(node: PathNode): boolean {
  return node.degree === 1 || node.degree === '1';
}

function isStructuredPathNodeArray(path: unknown[]): boolean {
  return path.length > 0 && path.some((item) => item !== null && typeof item === 'object');
}

function isPlaceholderStringPath(path: unknown[]): boolean {
  if (!path.every((node) => typeof node === 'string')) return false;
  const lowered = path.map((n) => String(n).trim().toLowerCase());
  return lowered.includes('mutual friend') || lowered.includes('friend of friend');
}

function extractStructuredPaths(s: Record<string, unknown>): PathNode[][] {
  const paths: PathNode[][] = [];

  const single = s.connectionPath;
  if (Array.isArray(single) && single.length >= 2 && isStructuredPathNodeArray(single) && !isPlaceholderStringPath(single)) {
    paths.push(single as PathNode[]);
  }

  const multi = s.connectionPaths;
  if (Array.isArray(multi)) {
    for (const entry of multi) {
      if (Array.isArray(entry) && entry.length >= 2 && isStructuredPathNodeArray(entry) && !isPlaceholderStringPath(entry)) {
        paths.push(entry as PathNode[]);
        continue;
      }
      if (entry && typeof entry === 'object') {
        const obj = entry as Record<string, unknown>;
        const people = obj.people;
        if (Array.isArray(people) && people.length >= 2 && isStructuredPathNodeArray(people)) {
          paths.push(people as PathNode[]);
        }
      }
    }
  }

  return paths;
}

function countUniqueDegree1StartPoints(paths: PathNode[][], targetId?: string): number {
  const seen = new Set<string>();
  for (const path of paths) {
    for (const raw of path) {
      if (!raw || typeof raw !== 'object') continue;
      const node = raw as PathNode;
      if (isViewerNode(node) || isTargetNode(node, targetId)) continue;
      if (!isDegree1Node(node)) continue;
      const key = nodeDedupeKey(node);
      if (key) seen.add(key);
    }
  }
  return seen.size;
}

function countFirstHopStartPoints(paths: PathNode[][], targetId?: string): number {
  const seen = new Set<string>();
  for (const path of paths) {
    if (path.length < 3) continue;
    const firstHop = path.find(
      (raw) =>
        raw &&
        typeof raw === 'object' &&
        !isViewerNode(raw as PathNode) &&
        !isTargetNode(raw as PathNode, targetId)
    ) as PathNode | undefined;
    if (!firstHop) continue;
    const key = nodeDedupeKey(firstHop);
    if (key) seen.add(key);
  }
  return seen.size;
}

function countStartPointsFromStructuredPaths(paths: PathNode[][], targetId?: string): number {
  const byDegree = countUniqueDegree1StartPoints(paths, targetId);
  if (byDegree > 0) return byDegree;
  return countFirstHopStartPoints(paths, targetId);
}

function countStartPointsFromDisplayedChain(
  pathNodes: PathNode[],
  targetId?: string,
  pathDegree?: number
): number {
  if (pathNodes.length < 2) return 0;

  const byDegree = countUniqueDegree1StartPoints([pathNodes], targetId);
  if (byDegree > 0) return byDegree;

  const degree = pathDegree ?? Math.max(1, pathNodes.length - 1);

  if (degree >= 3) {
    const firstHop = pathNodes.find((node) => !isViewerNode(node) && !isTargetNode(node, targetId));
    return firstHop ? 1 : 0;
  }

  if (degree === 2) {
    return pathNodes.filter((node) => !isViewerNode(node) && !isTargetNode(node, targetId)).length;
  }

  return 0;
}

function resolveTargetId(s: Record<string, unknown>, override?: string): string | undefined {
  if (override) return override;
  if (typeof s.userId === 'string' && s.userId) return s.userId;
  if (typeof s.id === 'string' && s.id) return s.id;
  return undefined;
}

function degreeFromStructuredPath(path: PathNode[]): number | undefined {
  if (path.length < 2) return undefined;

  const last = path[path.length - 1] as PathNode;
  const lastDeg = Number(last.degree);
  if (Number.isFinite(lastDeg) && lastDeg >= 1) return lastDeg;

  return path.length - 1;
}

function degreeFromStructuredPaths(paths: PathNode[][]): number | undefined {
  let maxDegree = 0;
  for (const path of paths) {
    const hop = degreeFromStructuredPath(path);
    if (hop !== undefined && hop > maxDegree) maxDegree = hop;
  }
  return maxDegree >= 1 ? maxDegree : undefined;
}

function readExplicitDegree(s: Record<string, unknown>): number | undefined {
  const raw = s.degree ?? s.connection_degree ?? s.degreeNumber;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 1) return Math.floor(raw);
  if (typeof raw === 'string') {
    if (raw === '1st' || raw === 'first') return 1;
    if (raw === '2nd' || raw === 'second') return 2;
    if (raw === '3rd' || raw === 'third') return 3;
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed >= 1) return Math.floor(parsed);
  }
  return undefined;
}

function readExplicitMutualCount(s: Record<string, unknown>): number | undefined {
  const keys = [
    'mutualFriendsCount',
    'mutual_friends_count',
    'mutualConnectionsCount',
    'mutual_connections_count',
    'sharedConnectionsCount',
    'shared_connections_count',
    'tiesCount',
    'connectionCount',
    'connection_count',
  ] as const;

  for (const key of keys) {
    const v = s[key];
    if (typeof v === 'number' && Number.isFinite(v)) {
      return Math.max(0, Math.floor(v));
    }
  }
  return undefined;
}

function readMutualFriendsArrayLength(s: Record<string, unknown>): number | undefined {
  const mutualArrayKeys = [
    'mutualFriends',
    'mutual_friends',
    'mutual_connections',
    'sharedConnections',
    'shared_connections',
    'ties',
  ] as const;

  let sawMalformedMutualArrayField = false;
  for (const key of mutualArrayKeys) {
    if (!Object.prototype.hasOwnProperty.call(s, key)) continue;
    const val = s[key];
    if (Array.isArray(val)) {
      if (val.length > 0) return val.length;
      continue;
    }
    sawMalformedMutualArrayField = true;
  }

  if (sawMalformedMutualArrayField) return 0;
  return undefined;
}

function buildMergedSource(input: ResolveConnectionPathSummaryInput): Record<string, unknown> {
  const profile = input.profile && typeof input.profile === 'object' ? input.profile : {};
  const network =
    input.networkConnection && typeof input.networkConnection === 'object'
      ? input.networkConnection
      : {};

  const merged: Record<string, unknown> = { ...profile, ...network };

  if (Array.isArray(input.connectionPath) && input.connectionPath.length >= 2) {
    merged.connectionPath = input.connectionPath;
  }

  return merged;
}

function pickStructuredPaths(
  merged: Record<string, unknown>,
  explicitPath?: unknown[] | null
): PathNode[][] {
  if (Array.isArray(explicitPath) && explicitPath.length >= 2 && isStructuredPathNodeArray(explicitPath)) {
    return [explicitPath as PathNode[]];
  }
  return extractStructuredPaths(merged);
}

function hasPathContext(structuredPaths: PathNode[][], pathNodes: unknown[] | null | undefined): boolean {
  if (structuredPaths.length > 0) return true;
  return Array.isArray(pathNodes) && pathNodes.length >= 2;
}

function connectionSelectionRank(
  conn: Record<string, unknown>,
  targetUserId?: string
): number {
  const hasPath =
    Array.isArray(conn.connectionPath) && (conn.connectionPath as unknown[]).length >= 2;
  const resolved = resolveConnectionPathSummary({
    networkConnection: conn,
    targetUserId,
  });
  return (hasPath ? 1000 : 0) + resolved.degree * 10 + resolved.mutualFriendCount;
}

/** Prefer structured connectionPath, then higher canonical degree from resolver. */
export function findBestNetworkConnection(
  connections: Array<Record<string, unknown>>
): Record<string, unknown> | null {
  if (!connections.length) return null;

  let best: Record<string, unknown> | null = null;
  let bestRank = -1;

  for (const conn of connections) {
    const userId = (conn.userId || conn.id) as string | undefined;
    const rank = connectionSelectionRank(conn, userId);
    if (rank > bestRank) {
      best = conn;
      bestRank = rank;
    }
  }

  return best;
}

export function findBestNetworkConnectionForUserId(
  getTabConnections: (tab: string) => Array<Record<string, unknown>>,
  targetUserId: string
): Record<string, unknown> | null {
  const matches = getTabConnections('all').filter(
    (conn) => conn.userId === targetUserId || conn.id === targetUserId
  );

  if (matches.length > 0) {
    return findBestNetworkConnection(matches);
  }

  const fallbackMatches = [
    ...getTabConnections('third'),
    ...getTabConnections('second'),
    ...getTabConnections('first'),
  ].filter((conn) => conn.userId === targetUserId || conn.id === targetUserId);

  return findBestNetworkConnection(fallbackMatches);
}

export type BestConnectionForTarget = ConnectionPathSummaryResult & {
  networkConnection: Record<string, unknown> | null;
  connectionPath?: unknown[];
};

/** Single canonical row + label for a target user — use on every surface. */
export function resolveBestConnectionForTarget(
  targetUserId: string,
  getTabConnections: (tab: string) => Array<Record<string, unknown>>
): BestConnectionForTarget {
  const networkConnection = findBestNetworkConnectionForUserId(getTabConnections, targetUserId);
  const resolved = resolveConnectionPathSummary({
    networkConnection,
    targetUserId,
  });
  const connectionPath = networkConnection?.connectionPath;
  return {
    ...resolved,
    networkConnection,
    connectionPath: Array.isArray(connectionPath) ? connectionPath : undefined,
  };
}

/**
 * V601: Synthesize avatar-chain nodes from a row's mutualFriends array so the canonical resolver
 * yields the same degree regardless of caller (inline AvatarChain vs. tab dedupe vs. Explore badge).
 * Mirrors the chain layout that ConnectionCard.AvatarChain builds for 2nd-degree rendering:
 * [viewer, ...mutualFriends, target]. Cap is enforced by the caller via MAX_SYNTH_DEGREE.
 */
function synthesizeAvatarNodesFromMutualFriends(merged: Record<string, unknown>): PathNode[] {
  const raw = merged.mutualFriends ?? merged.mutual_friends;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const friends = raw.filter((n) => n && typeof n === 'object') as PathNode[];
  if (friends.length === 0) return [];
  const viewer: PathNode = { isCurrentUser: true, name: 'You' };
  const target: PathNode = { isTargetUser: true };
  return [viewer, ...friends, target];
}

/** V601: Highest bucket the Network UI supports; matches first/second/third tabs only. */
const MAX_SYNTH_DEGREE = 3;

export function resolveConnectionPathSummary(
  input: ResolveConnectionPathSummaryInput
): ConnectionPathSummaryResult {
  const merged = buildMergedSource(input);
  const targetId = resolveTargetId(merged, input.targetUserId);
  const structuredPaths = pickStructuredPaths(merged, input.connectionPath);
  let avatarNodes = Array.isArray(input.avatarChainData)
    ? (input.avatarChainData.filter((n) => n && typeof n === 'object') as PathNode[])
    : [];

  // V601: When the caller did not supply a chain and the row has no structured path,
  // synthesize the same chain shape the inline UI builds so all surfaces agree.
  if (structuredPaths.length === 0 && avatarNodes.length === 0) {
    avatarNodes = synthesizeAvatarNodesFromMutualFriends(merged);
  }

  let degree: number | undefined;

  if (structuredPaths.length > 0) {
    degree = degreeFromStructuredPaths(structuredPaths);
  } else if (avatarNodes.length >= 2) {
    degree = Math.min(MAX_SYNTH_DEGREE, avatarNodes.length - 1);
  }

  const pathNodesForMutual =
    structuredPaths.length > 0 ? structuredPaths[0] : avatarNodes.length > 0 ? avatarNodes : [];

  let mutualFriendCount = 0;

  if (hasPathContext(structuredPaths, pathNodesForMutual)) {
    if (structuredPaths.length > 0) {
      mutualFriendCount = countStartPointsFromStructuredPaths(structuredPaths, targetId);
    } else if (avatarNodes.length > 0) {
      mutualFriendCount = countStartPointsFromDisplayedChain(
        avatarNodes,
        targetId,
        degree ?? avatarNodes.length - 1
      );
    }
    if ((degree ?? 0) >= 2) mutualFriendCount = Math.max(1, mutualFriendCount);
  } else {
    const explicitMutual = readExplicitMutualCount(merged);
    const arrayLen = readMutualFriendsArrayLength(merged);
    mutualFriendCount = explicitMutual ?? arrayLen ?? 0;
    if (degree === undefined) {
      degree = readExplicitDegree(merged);
    }
  }

  if (degree === undefined) {
    degree = readExplicitDegree(merged);
  }

  if (degree === undefined) {
    degree = 1;
  }

  const label = [formatDegree(degree), formatMutualFriends(mutualFriendCount)].filter(Boolean).join(' • ');

  return { degree, mutualFriendCount, label };
}

/** Deduplicate network rows by user — keep best row via findBestNetworkConnection + canonical degree. */
export function dedupeConnectionsForTabCache<T extends Record<string, unknown>>(connections: T[]): T[] {
  const groups = new Map<string, T[]>();

  for (const conn of connections) {
    const userId = (conn.userId || conn.id) as string | undefined;
    if (!userId) continue;
    const group = groups.get(userId) ?? [];
    group.push(conn);
    groups.set(userId, group);
  }

  const deduped: T[] = [];
  for (const [userId, group] of groups) {
    const best = findBestNetworkConnection(group as Array<Record<string, unknown>>);
    if (!best) continue;
    const resolved = resolveConnectionPathSummary({
      networkConnection: best,
      targetUserId: userId,
    });
    deduped.push({ ...best, degree: resolved.degree } as unknown as T);
  }

  return deduped;
}

export function getPathDegree(
  pathNodes: unknown[] | null | undefined,
  mutualSource?: MutualSource
): number {
  return resolveConnectionPathSummary({
    profile: mutualSource as Record<string, unknown>,
    avatarChainData: pathNodes,
  }).degree;
}

export function buildDisplayPathNodesForSummary(source: MutualSource): PathNode[] {
  const s = source && typeof source === 'object' ? (source as Record<string, unknown>) : {};
  const structured = extractStructuredPaths(s);
  if (structured.length > 0 && structured[0].length > 0) {
    return structured[0];
  }
  return [];
}

export function getMutualFriendCount(
  source: MutualSource,
  pathNodes: unknown[] | null | undefined
): number {
  return resolveConnectionPathSummary({
    profile: source as Record<string, unknown>,
    avatarChainData: pathNodes,
  }).mutualFriendCount;
}

export function buildConnectionPathSummary(
  mutualSource: MutualSource,
  pathNodes: unknown[] | null | undefined
): string {
  return resolveConnectionPathSummary({
    profile: mutualSource as Record<string, unknown>,
    avatarChainData: pathNodes,
  }).label;
}
